import { z } from "zod";

const CatalogProductTypeSchema = z.enum([
  "part_new",
  "part_oem",
  "part_used",
  "service",
]);
const CatalogProductStatusSchema = z.enum([
  "draft",
  "active",
  "hidden",
  "archived",
  "candidate",
]);
const CatalogSkuConditionSchema = z.enum(["new", "used", "refurb"]);
const CatalogIdParamSchema = z.object({
  id: z.string().uuid(),
});
const OptionalNullableIntSchema = z.preprocess((value) => {
  if (value === "" || value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? value : parsed;
  }
  return value;
}, z.number().int().nullable().optional());

const OptionalNullableMonthSchema = z.preprocess((value) => {
  if (value === "" || value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? value : parsed;
  }
  return value;
}, z.number().int().min(1).max(12).nullable().optional());

export const CatalogProductIdParamSchema = CatalogIdParamSchema;
export const CatalogCandidateProductIdParamSchema = CatalogIdParamSchema;
export const CatalogSkuMediaIdParamSchema = z.object({
  id: z.string().uuid(),
  mediaId: z.string().uuid(),
});
export const CatalogSkuMediaAddSchema = z.object({
  url: z.string().min(1, "ຕ້ອງສົ່ງ url (key) ຂອງຮູບ"),
});
export const CatalogSkuMediaReorderSchema = z.object({
  mediaIds: z
    .array(z.string().uuid())
    .min(1, "ຕ້ອງສົ່ງລາຍການ media_id ຢ່າງໜ້ອຍ 1 ອັນ"),
});
export const CatalogSkuIdParamSchema = CatalogIdParamSchema;
export const CatalogFitmentIdParamSchema = CatalogIdParamSchema;
export const CatalogBrandIdParamSchema = CatalogIdParamSchema;
export const CatalogVehicleMakeIdParamSchema = CatalogIdParamSchema;
export const CatalogVehicleModelIdParamSchema = CatalogIdParamSchema;
export const CatalogVehicleConfigurationIdParamSchema = CatalogIdParamSchema;

export const CatalogProductCreateSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ສິນຄ້າ"),
  type: CatalogProductTypeSchema,
  categoryId: z.string().uuid().nullable().optional(),
  brandId: z.string().uuid().nullable().optional(),
  description: z.string().nullable().optional(),
  status: CatalogProductStatusSchema.optional(),
});
export const CatalogProductUpdateSchema = z
  .object({
    name: z.string().min(1).optional(),
    type: CatalogProductTypeSchema.optional(),
    categoryId: z.string().uuid().nullable().optional(),
    brandId: z.string().uuid().nullable().optional(),
    description: z.string().nullable().optional(),
    status: CatalogProductStatusSchema.optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });

export const CatalogProductListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  status: CatalogProductStatusSchema.optional(),
  categoryId: z.string().uuid().optional(),
  brandId: z.string().uuid().optional(),
  type: CatalogProductTypeSchema.optional(),
  search: z.string().optional(),
});

export const CatalogSkuListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().optional(),
  condition: CatalogSkuConditionSchema.optional(),
});

/** ຄົ້ນ catalog SKU ທົ່ວໂພຣເຈັກຕາມ ຊື່ສິນຄ້າ / OEM / uuid */
export const CatalogSkuLookupListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().optional(),
});

export const CatalogSkuCreateSchema = z.object({
  catalogProductId: z.string().uuid(),
  oemPartNo: z.string().max(100).nullable().optional(),
  condition: CatalogSkuConditionSchema.nullable().optional(),
  fitmentStatus: z.enum(["pending", "complete"]).optional(),
});
export const CatalogSkuUpdateSchema = z
  .object({
    oemPartNo: z.string().max(100).nullable().optional(),
    condition: CatalogSkuConditionSchema.nullable().optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });

export const CatalogFitmentCreateSchema = z.object({
  catalogSkuId: z.string().uuid(),
  vehicleConfigurationId: z.string().uuid(),
  fitmentNote: z.string().max(8000).nullable().optional(),
  quantityPerVehicle: z.number().int().positive().nullable().optional(),
  applicabilityCode: z.string().max(100).nullable().optional(),
});

export const CatalogBrandListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().optional(),
});

export const CatalogBrandCreateSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ແບຣນດ໌"),
});
export const CatalogBrandUpdateSchema = z
  .object({
    name: z.string().min(1).optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });

export const VehicleMakeListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().optional(),
});

export const VehicleModelListQuerySchema = z.object({
  makeId: z.string().uuid().optional(),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export const VehicleConfigurationListQuerySchema = z.object({
  makeId: z.string().uuid().optional(),
  modelId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});

export const VehicleMakeCreateSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຍີ່ຫໍ້ລົດ"),
  logoKey: z.string().min(1).nullable().optional(),
});
export const VehicleMakeUpdateSchema = z
  .object({
    name: z.string().min(1).optional(),
    logoKey: z.string().min(1).nullable().optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });

export const VehicleModelCreateSchema = z.object({
  makeId: z.string().uuid(),
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຮຸ່ນ"),
});
export const VehicleModelUpdateSchema = z
  .object({
    makeId: z.string().uuid().optional(),
    name: z.string().min(1).optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });

export const VehicleModelCodeTypeSchema = z.enum(["spec", "option"]);

export const VehicleConfigurationCreateSchema = z.object({
  modelId: z.string().uuid(),
  modelCode: z.string().max(30).nullable().optional(),
  yearFrom: OptionalNullableIntSchema,
  yearTo: OptionalNullableIntSchema,
  monthFrom: OptionalNullableMonthSchema,
  monthTo: OptionalNullableMonthSchema,
  engine: z.string().max(255).nullable().optional(),
  marketCode: z.string().max(20).nullable().optional(),
  transmissionCode: z.string().max(50).nullable().optional(),
  engineCode: z.string().max(50).nullable().optional(),
  gradeCode: z.string().max(50).nullable().optional(),
  adminNotes: z.string().max(5000).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export const VehicleConfigurationUpdateSchema = z
  .object({
    modelId: z.string().uuid().optional(),
    modelCode: z.string().max(30).nullable().optional(),
    yearFrom: OptionalNullableIntSchema,
    yearTo: OptionalNullableIntSchema,
    monthFrom: OptionalNullableMonthSchema,
    monthTo: OptionalNullableMonthSchema,
    engine: z.string().max(255).nullable().optional(),
    marketCode: z.string().max(20).nullable().optional(),
    transmissionCode: z.string().max(50).nullable().optional(),
    engineCode: z.string().max(50).nullable().optional(),
    gradeCode: z.string().max(50).nullable().optional(),
    adminNotes: z.string().max(5000).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });

export const VehicleModelCodeIdParamSchema = CatalogIdParamSchema;

export const VehicleModelCodeCreateSchema = z.object({
  modelId: z.string().uuid(),
  codeType: VehicleModelCodeTypeSchema,
  code: z.string().min(1).max(50),
  label: z.string().max(255).nullable().optional(),
  notes: z.string().nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export const VehicleModelCodeUpdateSchema = z
  .object({
    codeType: VehicleModelCodeTypeSchema.optional(),
    code: z.string().min(1).max(50).optional(),
    label: z.string().max(255).nullable().optional(),
    notes: z.string().nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });

export const VehicleConfigurationCodeAssignmentsSetSchema = z.object({
  modelCodeIds: z.array(z.string().uuid()),
});

export const VehicleImportCommitQuerySchema = z.object({
  confirm: z
    .string()
    .optional()
    .transform((value) => value === "true" || value === "1"),
});

const NullableIsoDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .optional();

export const VehicleChassisRangeIdParamSchema = CatalogIdParamSchema;
export const CatalogSkuSupersessionIdParamSchema = CatalogIdParamSchema;
export const CatalogSkuAlternateIdParamSchema = CatalogIdParamSchema;
export const CatalogSkuSpecIdParamSchema = CatalogIdParamSchema;
export const EpcGroupIdParamSchema = CatalogIdParamSchema;
export const EpcDiagramIdParamSchema = CatalogIdParamSchema;
export const EpcDiagramPartIdParamSchema = CatalogIdParamSchema;

export const VehicleChassisRangeCreateSchema = z.object({
  vehicleConfigurationId: z.string().uuid(),
  vinPrefix: z.string().max(17).nullable().optional(),
  chassisPrefix: z.string().max(60).nullable().optional(),
  chassisFrom: z.string().max(60).nullable().optional(),
  chassisTo: z.string().max(60).nullable().optional(),
  productionFrom: NullableIsoDaySchema,
  productionTo: NullableIsoDaySchema,
  notes: z.string().max(500).nullable().optional(),
});
export type VehicleChassisRangeCreateInput = z.infer<
  typeof VehicleChassisRangeCreateSchema
>;

export const VehicleChassisRangeUpdateSchema = z
  .object({
    vinPrefix: z.string().max(17).nullable().optional(),
    chassisPrefix: z.string().max(60).nullable().optional(),
    chassisFrom: z.string().max(60).nullable().optional(),
    chassisTo: z.string().max(60).nullable().optional(),
    productionFrom: NullableIsoDaySchema,
    productionTo: NullableIsoDaySchema,
    notes: z.string().max(500).nullable().optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });
export type VehicleChassisRangeUpdateInput = z.infer<
  typeof VehicleChassisRangeUpdateSchema
>;

export const CatalogSkuSupersessionCreateSchema = z.object({
  fromPartNo: z.string().min(1).max(100),
  toPartNo: z.string().min(1).max(100),
  toCatalogSkuId: z.string().uuid().nullable().optional(),
  supersededAt: NullableIsoDaySchema,
  notes: z.string().nullable().optional(),
});
export type CatalogSkuSupersessionCreateInput = z.infer<
  typeof CatalogSkuSupersessionCreateSchema
>;

export const CatalogSkuAlternateTypeSchema = z.enum([
  "aftermarket",
  "oem_equivalent",
  "genuine_other",
]);

export const CatalogSkuAlternateCreateSchema = z.object({
  catalogSkuId: z.string().uuid(),
  altPartNo: z.string().min(1).max(100),
  altBrand: z.string().max(100).nullable().optional(),
  altType: CatalogSkuAlternateTypeSchema.nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type CatalogSkuAlternateCreateInput = z.infer<
  typeof CatalogSkuAlternateCreateSchema
>;

export const CatalogSkuSpecCreateSchema = z.object({
  catalogSkuId: z.string().uuid(),
  specKey: z.string().min(1).max(100),
  specValue: z.string().min(1).max(500),
  specUnit: z.string().max(50).nullable().optional(),
});
export type CatalogSkuSpecCreateInput = z.infer<
  typeof CatalogSkuSpecCreateSchema
>;

export const EpcGroupCreateSchema = z.object({
  vehicleConfigurationId: z.string().uuid(),
  parentGroupId: z.string().uuid().nullable().optional(),
  groupCode: z.string().max(50).nullable().optional(),
  groupName: z.string().min(1).max(255),
  sortOrder: z.number().int().min(0).optional(),
});
export type EpcGroupCreateInput = z.infer<typeof EpcGroupCreateSchema>;

export const EpcGroupUpdateSchema = z
  .object({
    parentGroupId: z.string().uuid().nullable().optional(),
    groupCode: z.string().max(50).nullable().optional(),
    groupName: z.string().min(1).max(255).optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });
export type EpcGroupUpdateInput = z.infer<typeof EpcGroupUpdateSchema>;

export const EpcDiagramCreateSchema = z.object({
  epcGroupId: z.string().uuid(),
  title: z.string().max(255).nullable().optional(),
  imageUrl: z.string().min(1, "ຕ້ອງສົ່ງ key ຮູບ ຫຼື URL ຮູບເຂົ້າລະບົບ"),
  sortOrder: z.number().int().min(0).optional(),
});
export type EpcDiagramCreateInput = z.infer<typeof EpcDiagramCreateSchema>;

export const EpcDiagramUpdateSchema = z
  .object({
    title: z.string().max(255).nullable().optional(),
    imageUrl: z.string().min(1).optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });
export type EpcDiagramUpdateInput = z.infer<typeof EpcDiagramUpdateSchema>;

export const EpcDiagramPartCreateSchema = z
  .object({
    epcDiagramId: z.string().uuid(),
    catalogProductId: z.string().uuid(),
    catalogSkuIds: z.array(z.string().uuid()).min(1, "ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 SKU"),
    calloutNumber: z.string().min(1).max(10),
    xPct: z.number().min(0).max(100),
    yPct: z.number().min(0).max(100),
    widthPct: z.number().min(0).max(100).nullable().optional(),
    heightPct: z.number().min(0).max(100).nullable().optional(),
    quantityPerVehicle: z.number().int().positive().nullable().optional(),
    applicabilityNote: z.string().nullable().optional(),
  })
  .superRefine((d, ctx) => {
    const w = d.widthPct;
    const h = d.heightPct;
    if (w == null && h == null) return;
    if (w == null || h == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງສົ່ງ ທັງ ກວ້າງ ແລະ ສູງ ພ້ອກກັນ (ຫຼື ບໍ່ສົ່ງ ທັງໝົດ ສໍາລັບຈຸດດຽວ)",
        path: ["widthPct"],
      });
      return;
    }
    if (w <= 0 || h <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ກວ້າງ ແລະ ສູງ ຕ້ອງຫຼາຍກວ່າ 0",
        path: ["widthPct"],
      });
    }
  });
export type EpcDiagramPartCreateInput = z.infer<
  typeof EpcDiagramPartCreateSchema
>;

export const EpcDiagramPartUpdateSchema = z
  .object({
    calloutNumber: z.string().min(1).max(10).optional(),
    catalogSkuIds: z.array(z.string().uuid()).min(1).optional(),
    xPct: z.number().min(0).max(100).optional(),
    yPct: z.number().min(0).max(100).optional(),
    widthPct: z.number().min(0).max(100).nullable().optional(),
    heightPct: z.number().min(0).max(100).nullable().optional(),
    quantityPerVehicle: z.number().int().positive().nullable().optional(),
    applicabilityNote: z.string().nullable().optional(),
  })
  .superRefine((d, ctx) => {
    const w = d.widthPct;
    const h = d.heightPct;
    if (w === undefined && h === undefined) return;
    if (w === undefined || h === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ກວ້າງ ແລະ ສູງ ຕ້ອງສົ່ງພ້ອມກັນ",
        path: ["widthPct"],
      });
      return;
    }
    if (w === null && h === null) return;
    if (typeof w === "number" && typeof h === "number") {
      if (w <= 0 || h <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ກວ້າງ ແລະ ສູງ ຕ້ອງຫຼາຍກວ່າ 0",
          path: ["widthPct"],
        });
      }
      return;
    }
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "ໃຊ້ ຄູ່ ຕົວເລກບວກ ສຳລັບກອ້ງ ຫຼື null ທັງຄູ່ເພື່ອລ້າງ",
      path: ["widthPct"],
    });
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "ຕ້ອງມີຂໍ້ມູນຢ່າງໜ້ອຍໜຶ່ງທີ່ຈະແກ້ໄຂ",
  });
export type EpcDiagramPartUpdateInput = z.infer<
  typeof EpcDiagramPartUpdateSchema
>;

export const CatalogProductMergeSchema = z.object({
  sourceProductId: z.string().uuid(),
  targetProductId: z.string().uuid(),
});

export const CatalogProductMergePreviewQuerySchema = z.object({
  sourceProductId: z.string().uuid(),
  targetProductId: z.string().uuid(),
});

export type CatalogProductMergeInput = z.infer<typeof CatalogProductMergeSchema>;
export type CatalogProductMergePreviewQuery = z.infer<
  typeof CatalogProductMergePreviewQuerySchema
>;

export type CatalogProductCreateInput = z.infer<
  typeof CatalogProductCreateSchema
>;
export type CatalogProductUpdateInput = z.infer<
  typeof CatalogProductUpdateSchema
>;
export type CatalogProductListQuery = z.infer<
  typeof CatalogProductListQuerySchema
>;
export type CatalogSkuListQuery = z.infer<typeof CatalogSkuListQuerySchema>;
export type CatalogSkuLookupListQuery = z.infer<
  typeof CatalogSkuLookupListQuerySchema
>;
export type CatalogSkuCreateInput = z.infer<typeof CatalogSkuCreateSchema>;
export type CatalogSkuUpdateInput = z.infer<typeof CatalogSkuUpdateSchema>;
export type CatalogFitmentCreateInput = z.infer<
  typeof CatalogFitmentCreateSchema
>;
export type CatalogBrandListQuery = z.infer<typeof CatalogBrandListQuerySchema>;
export type CatalogBrandCreateInput = z.infer<typeof CatalogBrandCreateSchema>;
export type CatalogBrandUpdateInput = z.infer<typeof CatalogBrandUpdateSchema>;
export type VehicleMakeListQuery = z.infer<typeof VehicleMakeListQuerySchema>;
export type VehicleMakeCreateInput = z.infer<typeof VehicleMakeCreateSchema>;
export type VehicleMakeUpdateInput = z.infer<typeof VehicleMakeUpdateSchema>;
export type VehicleModelCreateInput = z.infer<typeof VehicleModelCreateSchema>;
export type VehicleModelUpdateInput = z.infer<typeof VehicleModelUpdateSchema>;
export type VehicleModelListQuery = z.infer<typeof VehicleModelListQuerySchema>;
export type VehicleConfigurationCreateInput = z.infer<
  typeof VehicleConfigurationCreateSchema
>;
export type VehicleConfigurationUpdateInput = z.infer<
  typeof VehicleConfigurationUpdateSchema
>;
export type VehicleConfigurationListQuery = z.infer<
  typeof VehicleConfigurationListQuerySchema
>;
export type VehicleModelCodeCreateInput = z.infer<
  typeof VehicleModelCodeCreateSchema
>;
export type VehicleModelCodeUpdateInput = z.infer<
  typeof VehicleModelCodeUpdateSchema
>;
export type VehicleConfigurationCodeAssignmentsSetInput = z.infer<
  typeof VehicleConfigurationCodeAssignmentsSetSchema
>;
